import { z } from 'zod';

import { makeCursorPageSchema } from '../../common/cursor-page/cursor-page';
import { UuidSchema } from '../../common/id/id';
import { MessageModerationStatusSchema } from '../message-moderation-status/message-moderation-status';

/**
 * One message, as either member reads it.
 *
 * **`senderId` rather than a `mine` boolean.** The client already knows who it
 * is, and a server-computed "mine" would be a second place the answer lives —
 * the same reasoning that keeps `otherUserId` off this payload and on the
 * conversation.
 *
 * **`moderationStatus` is on the payload, and it is not decoration.** A message
 * withheld from its recipient is still shown to its sender (P20, N20), so the
 * sender's own client needs to know *why* the row it can see is marked; without
 * the field the only honest alternative would be to withhold it from the sender
 * too, which reads as the app losing a message.
 *
 * There is no `readAt`. Read state is a per-participant high-water mark on the
 * conversation (`otherLastReadAt`), because the timeline is ordered: a message
 * is read exactly when it is at or before that instant. A per-message flag would
 * be a write per message per reader for a fact the ordering already carries.
 */
export const MessageSchema = z
  .object({
    id: UuidSchema,
    conversationId: UuidSchema,
    senderId: UuidSchema,
    body: z.string().min(1),
    moderationStatus: MessageModerationStatusSchema,
    /**
     * When the server accepted it — the clock's, never the device's (N3).
     *
     * It is also half the ordering key, so it is the value a cursor is built
     * from and must round-trip exactly.
     */
    sentAt: z.iso.datetime(),
  })
  .strict();

export type Message = z.infer<typeof MessageSchema>;

/**
 * A page of history, newest first.
 *
 * Cursor-paginated rather than offset: a message arriving between two page
 * requests shifts every offset, and the member scrolling back would see a
 * duplicate — see `common/cursor-page`.
 */
export const MessagePageSchema = makeCursorPageSchema(MessageSchema);

export type MessagePage = z.infer<typeof MessagePageSchema>;
